import { useAtomValue } from 'jotai';

import { Stack, Text } from '@ekhodealer/ekho-common/rev';

import { AccordionSection } from '@common/components/AccordionSection';
import { InfoLineItem } from '@common/components/InfoLineItem';
import { formatCurrency } from '@common/lib/currency';

import { useGetPreorderDetails } from '../../api/useGetPreorderDetails';
import { preorderDetailsDrawerAtom } from '../../atoms/preorderDetailsDrawerAtoms';
import { StatusCell } from '../table-cells/StatusCell';

const SECTION_TITLE = 'Overview';

export const OverviewSection = (): React.ReactNode => {
	const { preorderId } = useAtomValue(preorderDetailsDrawerAtom);
	const { data: preorderDetails, isLoading } = useGetPreorderDetails(
		preorderId ?? undefined,
	);

	if (isLoading) {
		return (
			<AccordionSection title={SECTION_TITLE} defaultExpanded>
				<Stack itemsSpacing='16'>
					<InfoLineItem title='Preorder ID' isLoading />
					<InfoLineItem title='Status' isLoading />
					<InfoLineItem title='Deposit amount' isLoading />
					<InfoLineItem title='Deposit date' isLoading />
					<InfoLineItem title='Est. retail price' isLoading />
				</Stack>
			</AccordionSection>
		);
	}

	if (!preorderDetails) {
		return null;
	}

	const { overview } = preorderDetails;

	return (
		<AccordionSection title={SECTION_TITLE} defaultExpanded>
			<Stack itemsSpacing='16'>
				<InfoLineItem title='Preorder ID' value={overview.preorderId} isCopyable />
				<InfoLineItem title='Status' content={<StatusCell status={overview.status} />} />
				<InfoLineItem
					title='Deposit amount'
					content={
						<Text size='bodySmall' weight='semibold'>
							{formatCurrency(overview.depositAmount)}
						</Text>
					}
				/>
				<InfoLineItem title='Deposit date' value={overview.depositDate} />
				<InfoLineItem
					title='Est. retail price'
					content={
						<Text size='bodySmall' weight='semibold'>
							{formatCurrency(overview.estimatedRetailPrice)}
						</Text>
					}
				/>
				{overview.registrationState && (
					<InfoLineItem title='Registration state' value={overview.registrationState} />
				)}
				{overview.convertedCheckoutCartId && (
					<InfoLineItem
						title='Checkout cart ID'
						value={overview.convertedCheckoutCartId}
						isCopyable
					/>
				)}
			</Stack>
		</AccordionSection>
	);
};
